import type {CSSProperties} from 'react'

const FALLBACK = '#1F2D3D'
const INK = '#242424'

function parseHex(color: string): [number, number, number] | null {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color || '').trim())

    if (!match) {
        return null
    }

    const hex = match[1].length === 3 ? match[1].split('').map((c) => c + c).join('') : match[1]
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number]
}

function luminance([r, g, b]: [number, number, number]): number {
    const [lr, lg, lb] = [r, g, b].map((value) => {
        const v = value / 255
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb
}

export function contrastWithWhite(color: string): number {
    const rgb = parseHex(color)
    return rgb ? 1.05 / (luminance(rgb) + 0.05) : 0
}

/**
 * CSS variables for the reseller colour. Text on the colour switches to dark when the colour is too light for
 * white text, and icons use dark ink when the colour would be unreadable on white.
 */
export function themeVariables(color: string): CSSProperties {
    const rgb = parseHex(color) || parseHex(FALLBACK)
    const accent = `rgb(${rgb.join(', ')})`
    const lum = luminance(rgb)
    const whiteContrast = 1.05 / (lum + 0.05)
    const inkContrast = (lum + 0.05) / (luminance([36, 36, 36]) + 0.05)

    return {
        '--tt-accent': accent,
        '--tt-accent-soft': `rgba(${rgb.join(', ')}, 0.12)`,
        '--tt-on-accent': whiteContrast >= inkContrast ? '#FFFFFF' : INK,
        '--tt-accent-ink': whiteContrast >= 3 ? accent : INK
    } as CSSProperties
}
